/**
 * The book a bundle is made of (decision 7; part 24a, table N'): the open book, its title for the file's name, and this
 * device, once the open chapter's typing and the notes' typing (part 19d, N'4) have gone to the store, since the bundle
 * worker reads the store and not the editor or the workspace. Typing that can't be saved never stops the bundle (tenet
 * 3): the writer is told it isn't in it.
 */
import type { AutosaveResult } from '@cockatrice/editor';
import type { BundleRequest } from '../bundle/controller.ts';
import type { BookSummary } from './workspace.ts';

/** What the request is read from. */
export interface BundleSource {
  /** The open book, or null. */
  readonly book: BookSummary | null;
  /** This device's ID. */
  readonly device: string;
  /** Saves the open editor's typing now; null with no chapter open. */
  readonly flush: (() => Promise<AutosaveResult>) | null;
  /** N'4 (part 19d): stores the notes' typing now, after any write of it under way; whether none is left unstored. */
  readonly flushNotes: () => Promise<boolean>;
  /** The untitled book's title, for a book whose record can't be read. */
  readonly untitled: string;
}

/**
 * N'1–N'4: null with no book open; else the book, its title and this device, after the editor's save and then the notes'
 * (`unsaved` when either left typing out of the store).
 */
export async function bundleRequest(source: BundleSource): Promise<BundleRequest | null> {
  const { book } = source;
  if (book === null) return null;
  const saved = await source.flush?.();
  const notesStored = await source.flushNotes();
  return { bookId: book.id, device: source.device, title: book.metadata?.title ?? source.untitled, unsaved: saved?.kind === 'failed' || !notesStored };
}
